import React from "react";
import { StyledHorizontalDivider } from "../styled/styles";
import { primayColor } from "../styled/variable";
import PropTypes from "prop-types";
import { Formik } from "formik";
import { isEmpty, find } from "lodash";
import {
  Box,
  Button,
  Input,
  InputGroup,
  FormControl,
  FormErrorMessage,
  Flex,
  Text,
  InputRightElement,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { chakraComponents } from "chakra-react-select";
import TldrChakraSelect from "../common/TldrChakraSelect";
import ConfirmModal from "../confirmModal/ConfirmModal";
// User role drop down
const Option = (props) => {
  return (
    <>
      <chakraComponents.Option {...props}>
        <Flex direction="column" align="flex-start">
          <Text fontSize="sm" fontWeight={500}>
            {props.children}
          </Text>
          <Text fontSize="xs" color="gray.100" opacity="0.5">
            {props.data.description}
          </Text>
        </Flex>
      </chakraComponents.Option>
      <StyledHorizontalDivider />
    </>
  );
};

function UserInviteForm(props) {
  const { t } = useTranslation(["auth"]);
  const {
    roles,
    validationSchema,
    initialValues,
    helperText,
    confirmation,
    onConfirmation,
    onClose,
  } = props;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting, resetForm, setErrors }) => {
        setSubmitting(true);
        props.onInvite(values, { setSubmitting, resetForm, setErrors });
      }}
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setErrors,
        resetForm,
        setSubmitting,
      }) => (
        <>
          <Box
            my="4"
            display="flex"
            flexWrap="wrap"
            gap="1rem"
            justifyContent="flex-end"
           
          >
            <FormControl isInvalid={errors.email} flexGrow="1" width="auto">
              <InputGroup>
                <Input
                  px="2.5"
                  aria-label="Text input with dropdown button"
                  placeholder={t("user_invite.email")}
                  /* Set onChange to handleChange */
                  onChange={handleChange}
                  /* Set onBlur to handleBlur */
                  onBlur={handleBlur}
                  /* Store the value of this input in values.name, make sure this is named the same as the name property on the form element */
                  value={values.email}
                  name="email"
                />
                <InputRightElement
                  px={0}
                  whiteSpace="normal"
                  width="fit-content"
                  background="transparent"
                  borderLeft="1px solid"
                  borderColor="gray.1000"
                  _invalid={{ borderColor: "transparent" }}
                  children={
                    <TldrChakraSelect
                      value={find(roles, { id: values.role }) || null}
                      onChange={(selectedOption) => {
                        handleChange({
                          target: {
                            name: "role",
                            value: selectedOption.id,
                          },
                        });
                      }}
                      defaultValue={roles?.length > 0 && roles[0]}
                      options={roles}
                      chakraStyles={fileTypeSelectStyle}
                      placeholder={t("user_invite.role")}
                      components={{
                        Option,
                      }}
                      name="role"
                      isSearchable={false}
                      isInvalid={errors.email}
                      styles={{
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: "var(--chakra-zIndices-popover)",
                        }),
                      }}
                      menuPortalTarget={document.body}
                      classNamePrefix="chakra-react-select"
                    />
                  }
                />
              </InputGroup>
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <Button
              type="submit"
              disabled={isEmpty(values.email)}
              onClick={handleSubmit}
              variant="solid"
              isLoading={isSubmitting}
            >
              {t("user_invite.send_invite")}
            </Button>
          </Box>
          {confirmation && (
            <ConfirmModal
              isOpen
              title={t("user_invite.title")}
              message={helperText}
              onConfirm={() =>
                onConfirmation(values, { setSubmitting, resetForm, setErrors })
              }
              onClose={() => {
                setSubmitting(false);
                onClose();
              }}
              cancelButtonText={t("user_invite.cancel_btn")}
              confirmButtonText={t("user_invite.send_invite")}
            />
          )}
        </>
      )}
    </Formik>
  );
}

UserInviteForm.propTypes = {
  onInvite: PropTypes.func.isRequired,
};

export default UserInviteForm;

const fileTypeSelectStyle = {
  option: (provided, state) => ({
    ...provided,
    fontFamily: "Rubik",
    backgroundColor: "transparent",
    color: state.isSelected
      ? "primary.400"
      : state.isDisabled
      ? "gray.300"
      : "gray.100",
    cursor: state.isDisabled ? "not-allowed" : "inherit",
    _hover: {
      color: state.isSelected
        ? "primary.400"
        : state.isDisabled
        ? "gray.300"
        : "brand.white",
      backgroundColor: state.isDisabled ? "transparent" : "gray.1200",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "gray.300",
  }),
  input: (provided, state) => ({
    ...provided,
    color: "gray.100",
    lineHeight: "13px",
    fontSize: "14px",
  }),
  menu: (provided, state) => ({
    ...provided,
    background: primayColor,
    color: "gray.100",
    borderRadius: "4px",
    width: "300px",
  }),
  menuList: (provided, state) => ({
    ...provided,
    background: "brand.grey-darker",
    "::-webkit-scrollbar": {
      ...provided["::-webkit-scrollbar"],
      display: "none",
    },
  }),
  control: (provided, state) => ({
    ...provided,
    borderRadius: "8px",
    color: "gray.100",
    background: "transparent",
    border: "1px solid",
    borderColor: "transparent",
    minHeight: "32px",
    width: "100%",
    height: "38px",
    boxShadow: "none",
    borderTopLeftRadius: "unset",
    borderBottomLeftRadius: "unset",
    _focus: {
      borderColor: "gray.1000",
      background: "brand.darker",
      color: "brand.white",
    },
    _hover: {
      borderColor: "gray.1000",
      background: "brand.darker",
      color: "brand.white",
      cursor: "text",
    },
    _invalid: {
      border: "none",
    },
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    bg: null,
    color: "gray.100",
    paddingX: 2,
    fontSize: "xl",
    borderColor: "transparent",
    "> svg": {
      width: "1em",
      height: "1em",
    },
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: "0px 8px",
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    // height: "32px !important",
  }),
  indicatorContainer: (provided, state) => ({
    ...provided,
    color: "gray.100",
    ":hover": {
      ...provided[":hover"],
      color: "gray.100",
    },
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: "gray.100",
    fontFamily: "Rubik",
    position: "unset",
    top: "unset",
    transform: "unset",
    maxWidth: "calc(100% - 10px)",
    display: state.selectProps.menuIsOpen ? "none" : "block",
    lineHeight: "13px",
    fontSize: "14px",
  }),
  groupHeading: (provided, state) => ({
    ...provided,
    color: "brand.primary",
    fontFamily: "Rubik",
    fontWeight: "bold",
  }),
  group: (provided, state) => ({
    ...provided,
    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
  }),
  container: (provided, state) => ({
    ...provided,
    // padding: "0rem 1.5rem 0.25rem 1.5rem",
    minWidth: "116px",
  }),
};
